// =============================================================
// PublicShare.jsx — the PUBLIC (no-login) landing page for a DAM share link.
//
// Rendered by frontend/src/index.js when the path starts with /s/ — completely
// outside the MSAL-gated <App>. Reads the token from the URL, resolves the link,
// and shows one of two experiences:
//   • download — view the shared file and, for images, download it at a chosen
//                size (pixels and/or a target MB); or grab the original.
//   • upload   — an external upload portal: drag/drop files straight to storage.
//
// Part of the removable DAM module. Uses only brand tokens + the (provider-free)
// ui primitives + the unauthenticated publicApi — never the MSAL api client.
// =============================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { B, space, font, radius, weight, fonts } from '../../../constants/brand';
import { HarvusLogo } from '../../../components/shared/HarvusLogo';
import { Btn, Spinner, Icon } from '../../../components/ui';
import { resolveShare, buildDownloadUrl, uploadViaShare, formatBytes } from './publicApi';
import { filesFromDrop, folderOfFile } from '../folderDrop';
import { sanitizeNumericInput, WHOLE, UNSIGNED } from '../../../utils/numericInput';

// Pull the token out of /s/<token>.
function tokenFromPath() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  const i = parts.indexOf('s');
  return i >= 0 && parts[i + 1] ? decodeURIComponent(parts[i + 1]) : '';
}

const REASON_COPY = {
  not_found:      { title: 'Link not found', msg: 'This link is not valid. Please check the address, or ask the sender for a new one.' },
  expired:        { title: 'Link expired', msg: 'This share link has expired. Ask the sender to send you a new one.' },
  revoked:        { title: 'Link turned off', msg: 'This share link has been turned off by the sender.' },
  exhausted:      { title: 'Link limit reached', msg: 'This share link has reached its usage limit.' },
  asset_missing:  { title: 'File unavailable', msg: 'The shared file is no longer available.' },
  not_configured: { title: 'Not available', msg: 'Sharing is not set up on this server.' },
};

function triggerDownload(url) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ── Shell ───────────────────────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: B.appBg, fontFamily: fonts.sans,
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 16px' }}>
      <div style={{ marginBottom: space.xl }}><HarvusLogo height={34} color={B.navyDk} /></div>
      <div style={{ width: '100%', maxWidth: 640, background: B.surface, border: `1px solid ${B.border}`,
        borderRadius: radius.xxl, boxShadow: B.shadowMd, overflow: 'hidden' }}>
        {children}
      </div>
      <div style={{ marginTop: space.xl, fontSize: font.xs, color: B.textLt }}>
        Secured by Harvus · This is a private link — please don't share it.
      </div>
    </div>
  );
}

function Centered({ icon, title, msg, tone = B.textMd }) {
  return (
    <div style={{ padding: '56px 32px', textAlign: 'center' }}>
      <span style={{ display: 'inline-flex', color: tone, background: B.surfaceMuted, borderRadius: '50%', padding: 16 }}>
        <Icon name={icon} size={28} />
      </span>
      {title && <div style={{ marginTop: space.lg, fontSize: font.lg, fontWeight: weight.bold, color: B.text }}>{title}</div>}
      {msg && <div style={{ marginTop: space.sm, fontSize: font.md, color: B.textMd, maxWidth: 380, marginInline: 'auto', lineHeight: 1.6 }}>{msg}</div>}
    </div>
  );
}

// ── Top-level ─────────────────────────────────────────────────────────────────
export function PublicShare() {
  const [token] = useState(tokenFromPath);
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    if (!token) { setState({ loading: false, error: 'not_found' }); return; }
    resolveShare(token)
      .then(data => { if (live) setState({ loading: false, data }); })
      .catch(err => { if (live) setState({ loading: false, error: err.reason || 'not_found' }); });
    return () => { live = false; };
  }, [token]);

  let body;
  if (state.loading) body = <div style={{ padding: 40 }}><Spinner label="Loading…" /></div>;
  else if (state.error) {
    const c = REASON_COPY[state.error] || REASON_COPY.not_found;
    body = <Centered icon="warning" title={c.title} msg={c.msg} tone={B.amber} />;
  } else if (state.data?.kind === 'download') {
    body = <PublicDownload token={token} data={state.data} />;
  } else if (state.data?.kind === 'upload') {
    body = <PublicUpload token={token} data={state.data} />;
  } else {
    body = <Centered icon="warning" title="Not available" msg="This link can't be opened right now." tone={B.amber} />;
  }
  return <Shell>{body}</Shell>;
}

// ── Download experience ───────────────────────────────────────────────────────
const PRESET_DIMS = [480, 1024, 1920, 2560];
const PRESET_MB = [0.5, 1, 2, 5];

function PublicDownload({ token, data }) {
  const { asset, allowResize, recipientName, previewUrl } = data;
  const [mode, setMode] = useState('original');   // original | dims | size
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [maxMB, setMaxMB] = useState('');

  const doDownload = () => {
    let params = {};
    if (allowResize && mode === 'dims') params = { width: width || undefined, height: height || undefined };
    else if (allowResize && mode === 'size') params = { maxMB: maxMB || undefined };
    triggerDownload(buildDownloadUrl(token, params));
  };

  const canResizeSubmit = mode === 'original'
    || (mode === 'dims' && (width || height))
    || (mode === 'size' && maxMB);

  return (
    <div>
      <div style={{ padding: '24px 28px 0' }}>
        <div style={{ fontSize: font.sm, color: B.textMd }}>
          {recipientName ? `Hi ${recipientName} — a` : 'A'} file has been shared with you
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: font.xl, fontWeight: weight.bold, color: B.text, letterSpacing: -0.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {asset.title || asset.fileName}
        </h1>
      </div>

      {/* Preview */}
      <div style={{ margin: '18px 28px', background: B.surfaceSunken, borderRadius: radius.xxl, border: `1px solid ${B.border}`,
        minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {asset.isImage && previewUrl
          ? <img src={previewUrl} alt={asset.title || asset.fileName} style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain', display: 'block' }} />
          : <div style={{ textAlign: 'center', color: B.textMd, padding: 40 }}>
              <Icon name="file" size={48} color={B.textLt} />
              <div style={{ marginTop: space.sm, fontSize: font.sm }}>{(asset.fileType || '').toUpperCase()} file</div>
            </div>}
      </div>

      {/* Meta */}
      <div style={{ padding: '0 28px', fontSize: font.sm, color: B.textMd, display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
        <span><strong style={{ color: B.text }}>File:</strong> {asset.fileName}</span>
        {asset.fileSizeBytes != null && <span><strong style={{ color: B.text }}>Size:</strong> {formatBytes(asset.fileSizeBytes)}</span>}
        {asset.width && asset.height ? <span><strong style={{ color: B.text }}>Dimensions:</strong> {asset.width} × {asset.height}px</span> : null}
      </div>

      {/* Resize controls */}
      {allowResize && (
        <div style={{ margin: '18px 28px 0', padding: space.lg, background: B.surfaceMuted, borderRadius: radius.xxl, border: `1px solid ${B.divider}` }}>
          <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text, marginBottom: space.sm }}>
            Choose a size
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: space.md }}>
            {[['original', 'Original'], ['dims', 'By dimensions (px)'], ['size', 'By file size (MB)']].map(([id, label]) => (
              <button key={id} onClick={() => setMode(id)} style={{
                padding: '6px 12px', borderRadius: radius.pill, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: font.sm, fontWeight: weight.medium,
                border: `1px solid ${mode === id ? B.navy : B.border}`,
                background: mode === id ? B.navy : B.surface, color: mode === id ? B.white : B.textMd,
              }}>{label}</button>
            ))}
          </div>

          {mode === 'dims' && (
            <div>
              <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" inputMode="numeric" value={width} onChange={e => setWidth(sanitizeNumericInput(e.target.value, WHOLE))} placeholder="Width" style={numInput} />
                <span style={{ color: B.textLt }}>×</span>
                <input type="text" inputMode="numeric" value={height} onChange={e => setHeight(sanitizeNumericInput(e.target.value, WHOLE))} placeholder="Height" style={numInput} />
                <span style={{ fontSize: font.xs, color: B.textLt }}>px</span>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: space.sm }}>
                {PRESET_DIMS.map(px => (
                  <button key={px} onClick={() => { setWidth(String(px)); setHeight(''); }} style={presetBtn}>{px}px wide</button>
                ))}
              </div>
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 6 }}>
                Leave one field blank to keep the aspect ratio. Images are only scaled down, never up.
              </div>
            </div>
          )}

          {mode === 'size' && (
            <div>
              <div style={{ display: 'flex', gap: space.sm, alignItems: 'center' }}>
                {/* Sub-1 MB is the designed case (min 0.05, and 0.5 is a
                    preset), so ".5" is how anyone types half a megabyte — and
                    the Download button is gated on this box being non-empty,
                    with nothing on screen if it is not. What a number input
                    does to a leading point is engine-specific, so it should not
                    be what decides whether the button works. */}
                <input type="text" inputMode="decimal" value={maxMB} onChange={e => setMaxMB(sanitizeNumericInput(e.target.value, UNSIGNED))} placeholder="Max size" style={numInput} />
                <span style={{ fontSize: font.xs, color: B.textLt }}>MB</span>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: space.sm }}>
                {PRESET_MB.map(mb => (
                  <button key={mb} onClick={() => setMaxMB(String(mb))} style={presetBtn}>{mb} MB</button>
                ))}
              </div>
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 6 }}>
                We'll compress the image to land under your target size.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action */}
      <div style={{ padding: '20px 28px 28px', display: 'flex', justifyContent: 'flex-end' }}>
        <Btn onClick={doDownload} disabled={!canResizeSubmit}>
          <Icon name="download" size={15} /> Download{allowResize && mode !== 'original' ? ' resized' : ''}
        </Btn>
      </div>
    </div>
  );
}

// ── Upload experience ─────────────────────────────────────────────────────────
const STATUS_COLOR = { pending: B.textLt, uploading: B.amber, done: B.green, error: B.red };

function PublicUpload({ token, data }) {
  const { recipientName, requestedSpec, collectionName, maxUploadBytes } = data;
  const [items, setItems] = useState([]);       // { file, status, message }
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const folderInputRef = useRef(null);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setItems(prev => [...prev, ...files.map(file => {
      const tooBig = maxUploadBytes && file.size > maxUploadBytes;
      return { file, status: tooBig ? 'error' : 'pending', message: tooBig ? `Over the ${formatBytes(maxUploadBytes)} limit` : '', progress: 0 };
    })]);
  }, [maxUploadBytes]);

  // A dropped FOLDER is walked through the File System Entry API — `dataTransfer
  // .files` is flat and on most browsers holds none of a folder's contents, so
  // dropping one used to add nothing at all. Each file carries the path it came
  // in with, which publicApi sends on so the structure survives the upload.
  const onDrop = useCallback(async (e) => {
    e.preventDefault(); setDragging(false);
    addFiles(await filesFromDrop(e.dataTransfer));
  }, [addFiles]);

  // Upload (or resume) one queued file. Block staging persists progress to
  // localStorage, so Retry on an errored file continues rather than restarting.
  const uploadOne = async (i, file) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'uploading', message: '', progress: it.progress || 0 } : it));
    try {
      await uploadViaShare(token, file, {
        onProgress: ({ loaded, total }) => {
          const p = total ? loaded / total : 0;
          setItems(prev => prev.map((it, idx) => {
            if (idx !== i) return it;
            if (it.progress != null && p < 1 && Math.abs(p - it.progress) < 0.01) return it;
            return { ...it, progress: p };
          }));
        },
      });
      setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'done', message: 'Uploaded', progress: 1 } : it));
    } catch (e) {
      const msg = e.status === 413 ? 'File is too large' : (e.message || 'Upload failed');
      setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'error', message: msg } : it));
    }
  };

  const runUpload = async () => {
    if (busy) return;
    const pending = items.filter(it => it.status === 'pending');
    if (!pending.length) return;
    setBusy(true);
    for (let i = 0; i < items.length; i++) {
      if (items[i].status !== 'pending') continue;
      await uploadOne(i, items[i].file);
    }
    setBusy(false);
  };

  const retryOne = async (i, file) => {
    if (busy) return;
    setBusy(true);
    await uploadOne(i, file);
    setBusy(false);
  };

  const doneCount = items.filter(it => it.status === 'done').length;
  const pendingCount = items.filter(it => it.status === 'pending').length;
  const allDone = items.length > 0 && items.every(it => it.status === 'done' || it.status === 'error') && pendingCount === 0;

  return (
    <div style={{ padding: '24px 28px 28px' }}>
      <div style={{ fontSize: font.sm, color: B.textMd }}>
        {recipientName ? `Hi ${recipientName} —` : ''} you've been asked to upload files
      </div>
      <h1 style={{ margin: '4px 0 0', fontSize: font.xl, fontWeight: weight.bold, color: B.text, letterSpacing: -0.3 }}>
        Upload files{collectionName ? ` to “${collectionName}”` : ''}
      </h1>

      {requestedSpec && (
        <div style={{ marginTop: space.md, padding: '12px 16px', background: B.tealLt, borderRadius: radius.lg,
          color: B.navyDk, fontSize: font.sm, display: 'flex', gap: space.sm, alignItems: 'flex-start' }}>
          <span style={{ color: B.navy, marginTop: 1 }}><Icon name="info" size={15} /></span>
          <span><strong>What's needed:</strong> {requestedSpec}</span>
        </div>
      )}

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{ marginTop: space.lg, border: `2px dashed ${dragging ? B.navy : B.border}`,
          background: dragging ? B.surfaceMuted : B.surfaceSunken, borderRadius: radius.xxl,
          padding: '32px 20px', textAlign: 'center', cursor: 'pointer', transition: 'border-color .15s, background .15s' }}
      >
        <Icon name="upload" size={28} color={B.navy} />
        <div style={{ marginTop: space.sm, fontWeight: weight.semibold, color: B.text }}>
          Drag & drop files or folders here, or click to browse
        </div>
        <div style={{ marginTop: 4, fontSize: font.sm, color: B.textMd }}>
          {maxUploadBytes ? `Up to ${formatBytes(maxUploadBytes)} per file.` : 'Images, video, PDFs, docs and more.'}
          {' '}Folders keep their structure.
        </div>
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        {/* A directory pick populates each File's `webkitRelativePath`; the
            dropzone's own click must not also open this one. */}
        <input ref={folderInputRef} type="file" multiple hidden webkitdirectory=""
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <button type="button"
          onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
          style={{ marginTop: space.md, border: `1px solid ${B.border}`, background: B.surface,
            borderRadius: radius.lg, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: font.sm, fontWeight: weight.semibold, color: B.navy }}>
          Add a folder
        </button>
      </div>

      {/* Queue */}
      {items.length > 0 && (
        <div style={{ marginTop: space.lg, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
          {items.map((it, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: space.md, padding: '8px 12px',
              background: B.surfaceMuted, borderRadius: radius.lg, border: `1px solid ${B.border}` }}>
              <Icon name="file" size={18} color={B.textMd} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: font.base, color: B.text, fontWeight: weight.medium,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.file.name}</div>
                {folderOfFile(it.file) && (
                  <div style={{ fontSize: font.xs, color: B.textLt, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folderOfFile(it.file)}</div>
                )}
                <div style={{ fontSize: font.xs, color: it.status === 'error' ? B.red : B.textMd }}>
                  {it.status === 'uploading'
                    ? `${Math.round((it.progress || 0) * 100)}% · ${formatBytes((it.progress || 0) * it.file.size)} / ${formatBytes(it.file.size)}`
                    : `${formatBytes(it.file.size)}${it.message ? ` · ${it.message}` : ''}`}
                </div>
                {it.status === 'uploading' && (
                  <div style={{ marginTop: 5, height: 4, borderRadius: 2, background: B.border, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.round((it.progress || 0) * 100)}%`,
                      background: B.navy, transition: 'width .15s' }} />
                  </div>
                )}
              </div>
              {it.status === 'error' && !(maxUploadBytes && it.file.size > maxUploadBytes) && (
                <button type="button" onClick={() => retryOne(i, it.file)} disabled={busy}
                  style={{ fontSize: font.xs, fontWeight: weight.semibold, color: B.navy, background: 'none',
                    border: 'none', cursor: busy ? 'default' : 'pointer', padding: '2px 4px' }}>Retry</button>
              )}
              <span style={{ fontSize: font.xs, fontWeight: weight.semibold, color: STATUS_COLOR[it.status], textTransform: 'capitalize' }}>{it.status}</span>
            </div>
          ))}
        </div>
      )}

      {allDone ? (
        <div style={{ marginTop: space.lg, padding: '14px 16px', background: B.green + '12', border: `1px solid ${B.green}33`,
          borderRadius: radius.lg, color: B.green, fontSize: font.sm, display: 'flex', alignItems: 'center', gap: space.sm }}>
          <Icon name="check" size={16} /> Thanks! {doneCount} file{doneCount === 1 ? '' : 's'} uploaded. You can close this page.
        </div>
      ) : (
        <div style={{ marginTop: space.lg, display: 'flex', justifyContent: 'flex-end' }}>
          <Btn onClick={runUpload} disabled={!pendingCount || busy} loading={busy}>
            {busy ? 'Uploading…' : `Upload ${pendingCount || ''}`.trim()}
          </Btn>
        </div>
      )}
    </div>
  );
}

const numInput = {
  width: 110, padding: '8px 10px', fontSize: font.base, fontFamily: 'inherit',
  border: `1px solid ${B.border}`, borderRadius: radius.md, color: B.text, background: B.surface, outline: 'none',
};
const presetBtn = {
  padding: '4px 10px', borderRadius: radius.pill, cursor: 'pointer', fontFamily: 'inherit',
  fontSize: font.xs, fontWeight: weight.medium, border: `1px solid ${B.border}`, background: B.surface, color: B.textMd,
};
